import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { FileSearch, Loader2, Download } from "lucide-react";
import logger from "../lib/logger";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const STATUS_OPTS = [
  { v: "all", label: "All" },
  { v: "pending", label: "Pending" },
  { v: "approved", label: "Approved" },
  { v: "denied", label: "Denied" },
];

export default function OverrideAuditReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [start, setStart] = useState(monthAgo);
  const [end, setEnd] = useState(today);
  const [status, setStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status };
      if (start) params.start = `${start}T00:00:00`;
      if (end) params.end = `${end}T23:59:59.999`;
      const { data } = await api.get("/reports/overrides", { params });
      setRows(data);
    } catch (err) {
      logger.error("Override report failed:", err);
    } finally {
      setLoading(false);
    }
  }, [start, end, status]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => ({
    total: rows.length,
    approved: rows.filter((r) => r.status === "approved").length,
    denied: rows.filter((r) => r.status === "denied").length,
    pending: rows.filter((r) => r.status === "pending").length,
  }), [rows]);

  const downloadCsv = () => {
    const headers = ["created_at", "worker_name", "site_name", "distance_m", "status", "approver_name", "approved_at", "reason"];
    const lines = [headers.join(",")];
    for (const r of rows) {
      lines.push(headers.map((h) => {
        const v = r[h] ?? "";
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      }).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `override-audit-${start}_to_${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section
      className="border border-gray-300 bg-white rounded-sm"
      data-testid="override-audit-report"
    >
      <header className="flex items-center gap-2 px-5 py-3 border-b border-gray-200">
        <FileSearch className="w-5 h-5 text-[#002FA7]" />
        <h3 className="font-black text-lg tracking-tight">Override audit report</h3>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        <button
          onClick={downloadCsv}
          disabled={!rows.length}
          data-testid="export-overrides-csv"
          className="ml-auto px-3 py-1.5 border border-gray-300 text-xs font-bold rounded-sm flex items-center gap-1 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-50"
        >
          <Download className="w-3.5 h-3.5" /> CSV
        </button>
      </header>

      <div className="px-5 py-3 border-b border-gray-200 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">From</label>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            data-testid="report-start"
            className="border border-gray-300 px-3 py-2 rounded-sm text-sm font-mono"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">To</label>
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            data-testid="report-end"
            className="border border-gray-300 px-3 py-2 rounded-sm text-sm font-mono"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="report-status"
            className="border border-gray-300 px-3 py-2 rounded-sm bg-white text-sm"
          >
            {STATUS_OPTS.map((o) => (<option key={o.v} value={o.v}>{o.label}</option>))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]" />
        <div className="text-xs text-gray-600 font-mono">
          {totals.total} record(s) · {totals.approved} approved · {totals.denied} denied · {totals.pending} pending
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-[10px] uppercase tracking-widest text-gray-500">
            <tr>
              <th className="text-left px-4 py-2.5">Requested</th>
              <th className="text-left px-4 py-2.5">Team Member</th>
              <th className="text-left px-4 py-2.5">Site</th>
              <th className="text-left px-4 py-2.5">Distance</th>
              <th className="text-left px-4 py-2.5">Reason</th>
              <th className="text-left px-4 py-2.5">Decision</th>
              <th className="text-left px-4 py-2.5">By</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400 italic">No override records in this range.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100" data-testid={`audit-row-${r.id}`}>
                <td className="px-4 py-2 font-mono text-xs">{fmt(r.created_at)}</td>
                <td className="px-4 py-2 text-sm font-semibold">{r.worker_name}</td>
                <td className="px-4 py-2 text-sm">{r.site_name}</td>
                <td className="px-4 py-2 font-mono text-xs">{r.distance_m != null ? `${Math.round(r.distance_m)} m` : "—"}</td>
                <td className="px-4 py-2 text-xs text-gray-600 italic max-w-[260px] truncate" title={r.reason || ""}>
                  {r.reason || "—"}
                </td>
                <td className="px-4 py-2">
                  <StatusBadge status={r.status} />
                </td>
                <td className="px-4 py-2 text-xs font-mono">
                  {r.status === "approved" && (
                    <>
                      <div>{r.approver_name || "—"}</div>
                      <div className="text-gray-400">{fmt(r.approved_at)}</div>
                    </>
                  )}
                  {r.status === "denied" && (
                    <>
                      <div>{r.denier_name || "—"}</div>
                      <div className="text-gray-400">{fmt(r.denied_at)}</div>
                    </>
                  )}
                  {r.status === "pending" && <span className="text-gray-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: { c: "bg-green-100 text-green-900 border-green-200", t: "Approved" },
    denied: { c: "bg-red-100 text-red-800 border-red-200", t: "Denied" },
    pending: { c: "bg-amber-100 text-amber-900 border-amber-200", t: "Pending" },
  };
  const m = map[status] || { c: "bg-gray-100 text-gray-700 border-gray-200", t: status || "—" };
  return (
    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-sm border ${m.c}`}>
      {m.t}
    </span>
  );
}
